import React, { useState, useEffect } from 'react';
import { botService, permissionService } from '../services';
import BackButton from '../components/BackButton';
import '../styles/App.css';

const QQBotConfig = () => {
  const [loading, setLoading] = useState(true);
  const [botStatus, setBotStatus] = useState({ connected: false, hasConfig: false });
  const [config, setConfig] = useState(null);
  const [userLimits, setUserLimits] = useState({ max_rooms: 0, max_qq_groups: 0, current_rooms: 0, isAdmin: false });
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // View state
  const [view, setView] = useState('list'); // 'list' or 'detail'
  const [selectedRoomIndex, setSelectedRoomIndex] = useState(null);

  useEffect(() => {
    fetchBotData();
    // Poll bot status every 10 seconds
    const interval = setInterval(fetchBotStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchBotStatus = async () => {
    try {
      const status = await botService.getBotStatus();
      setBotStatus(status);
    } catch (error) {
      console.error('Error fetching bot status:', error);
    }
  };

  const fetchBotData = async () => {
    setLoading(true);
    setError('');
    try {
      const [status, configData, limits] = await Promise.all([
        botService.getBotStatus(),
        botService.getBotConfig(),
        permissionService.getMyLimits()
      ]);
      setBotStatus(status);
      setConfig(configData);
      setUserLimits(limits);
      if (!configData.rooms) {
        setConfig({ rooms: [] });
      }
    } catch (error) {
      console.error('Error fetching bot data:', error);
      setError(error.response?.data?.message || '无法连接到 Bot，请确保 Bot 已启动并连接到服务器');
      try {
        const status = await botService.getBotStatus();
        setBotStatus(status);
      } catch (e) {
        console.error('Error fetching status:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async (updatedConfig) => {
    setSaveLoading(true);
    setError('');
    setSuccess('');

    try {
      await botService.updateBotConfig(updatedConfig);
      setConfig(updatedConfig);
      setSuccess('配置已成功更新！Bot 会自动重载配置。');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving config:', error);
      setError(error.response?.data?.message || '保存配置失败');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleToggleQQNotify = async (index) => {
    const updatedConfig = { ...config };
    updatedConfig.rooms[index].qq_notify_enabled = !updatedConfig.rooms[index].qq_notify_enabled;
    await handleSaveConfig(updatedConfig);
  };

  const handleUpdateRoom = async (index, updates) => {
    const updatedConfig = { ...config };
    updatedConfig.rooms[index] = { ...updatedConfig.rooms[index], ...updates };
    await handleSaveConfig(updatedConfig);
  };

  const handleRefresh = () => {
    fetchBotData();
  };

  if (loading) {
    return (
      <div className="container">
        <BackButton />
        <div className="admin-container">
          <h1>QQ 推送配置</h1>
          <div className="loading">加载中...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <BackButton />
      <div className="admin-container">
        <div className="admin-header">
          <h1>QQ 推送配置</h1>
          <div className="bot-status-indicator">
            <span className={`status-dot ${botStatus.connected ? 'status-connected' : 'status-disconnected'}`}></span>
            <span className="status-text">
              {botStatus.connected ? 'Bot 已连接' : 'Bot 未连接'}
            </span>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            {success}
          </div>
        )}

        {!botStatus.connected && (
          <div className="alert alert-warning">
            ⚠️ Bot 未连接到服务器。请确保 Bot 程序正在运行并配置了正确的 WebSocket 地址。
          </div>
        )}

        <div className="bot-config-actions">
          <button 
            className="btn btn-primary" 
            onClick={handleRefresh}
            disabled={!botStatus.connected}
          >
            🔄 刷新配置
          </button>
          
          <a href="/admin/bot" className="btn btn-secondary">
            ← 返回直播 Bot 配置
          </a>
        </div>

        {view === 'list' ? (
          <QQRoomListView 
            config={config}
            botStatus={botStatus}
            onSelectRoom={(index) => {
              setSelectedRoomIndex(index);
              setView('detail');
            }}
            onToggleQQNotify={handleToggleQQNotify}
          />
        ) : (
          <QQRoomDetailView 
            room={config?.rooms[selectedRoomIndex]}
            roomIndex={selectedRoomIndex}
            onBack={() => {
              setView('list');
              setSelectedRoomIndex(null);
            }}
            onUpdate={(updates) => handleUpdateRoom(selectedRoomIndex, updates)}
            saveLoading={saveLoading}
            userLimits={userLimits}
          />
        )}
      </div>
    </div>
  );
};

// QQ Room List View Component
const QQRoomListView = ({ config, botStatus, onSelectRoom, onToggleQQNotify }) => {
  if (!config?.rooms?.length) {
    return (
      <div className="no-config">
        <p>暂无配置的房间</p>
        <p className="help-text">请先在直播 Bot 配置中添加房间</p>
      </div>
    );
  }

  return (
    <div className="room-list">
      {config.rooms.map((room, index) => (
        <div key={index} className={`room-card ${room.qq_notify_enabled ? 'room-enabled' : 'room-disabled'}`}>
          <div className="room-card-header">
            {room.anchor_info?.face ? (
              <img 
                src={room.anchor_info.face} 
                alt={room.anchor_info?.uname || ''}
                className="anchor-avatar"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="anchor-avatar-placeholder">
                👤
              </div>
            )}
            
            <div className="room-card-title-area">
              <div className="anchor-name">{room.anchor_info?.uname || `UID: ${room.uid}`}</div>
              <h3>房间 {room.anchor_info?.room_id || room.uid}</h3>
            </div>

            <label className="toggle-switch" onClick={(e) => e.stopPropagation()}>
              <input 
                type="checkbox" 
                checked={room.qq_notify_enabled || false}
                onChange={() => onToggleQQNotify(index)}
                disabled={!botStatus.connected}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
          
          <div className="room-card-body" onClick={() => onSelectRoom(index)}>
            <div className="qq-targets-summary">
              <div className="qq-targets-count">
                <span className="qq-icon">📱</span>
                <span>已配置 {room.qq_targets?.length || 0} 个推送目标</span>
              </div>

              {/* 直播间功能状态提示 */}
              <div className="room-status-indicator">
                <span className={`status-badge ${room.enabled ? 'status-enabled' : 'status-disabled'}`}>
                  {room.enabled ? '🎙️ 直播间功能已开启' : '💤 直播间功能未开启'}
                </span>
              </div>

              {room.qq_targets && room.qq_targets.length > 0 && (
                <div className="qq-targets-preview">
                  {room.qq_targets.slice(0, 3).map((target, idx) => (
                    <span key={idx} className="qq-target-badge">
                      {target.type === 1 ? '群' : '私聊'}: {target.id}
                    </span>
                  ))}
                  {room.qq_targets.length > 3 && (
                    <span className="qq-target-badge qq-target-more">
                      +{room.qq_targets.length - 3} 更多
                    </span>
                  )}
                </div>
              )}
            </div>
            
            <div className="room-card-footer">
              <span className="click-hint">点击配置推送目标 →</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// QQ Room Detail View Component
const QQRoomDetailView = ({ room, roomIndex, onBack, onUpdate, saveLoading, userLimits }) => {
  const [editingTarget, setEditingTarget] = useState(null);
  const [showAddTarget, setShowAddTarget] = useState(false);

  if (!room) return null;

  const currentTargetCount = room.qq_targets?.length || 0;
  const maxQQGroups = userLimits?.max_qq_groups || 0;
  const isAdmin = userLimits?.isAdmin || false;
  const canAddMore = isAdmin || maxQQGroups === -1 || currentTargetCount < maxQQGroups;

  const handleToggleQQNotify = () => {
    onUpdate({ qq_notify_enabled: !room.qq_notify_enabled });
  };

  const handleAddTarget = (newTarget) => {
    if (!canAddMore) {
      alert(`您最多只能添加 ${maxQQGroups} 个推送目标`);
      return;
    }
    const targets = [...(room.qq_targets || []), newTarget];
    onUpdate({ qq_targets: targets });
    setShowAddTarget(false);
  };

  const handleUpdateTarget = (index, updatedTarget) => {
    const targets = [...(room.qq_targets || [])];
    targets[index] = updatedTarget;
    onUpdate({ qq_targets: targets });
    setEditingTarget(null);
  };

  const handleDeleteTarget = (index) => {
    if (!window.confirm('确定要删除这个推送目标吗？')) return;
    const targets = [...(room.qq_targets || [])];
    targets.splice(index, 1);
    onUpdate({ qq_targets: targets });
  };

  return (
    <div className="room-detail">
      <div className="room-detail-header">
        <button className="btn btn-secondary" onClick={onBack}>
          ← 返回房间列表
        </button>
        <h2>
          {room.anchor_info?.uname || `房间 ${room.anchor_info?.room_id || room.uid}`} - QQ 推送配置
        </h2>
      </div>

      {/* 总开关 */}
      <div className="feature-card">
        <div className="feature-card-header">
          <div className="feature-card-title">
            <span className="feature-icon">📱</span>
            <h3>QQ 推送总开关</h3>
          </div>
          <div className="feature-card-actions">
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={room.qq_notify_enabled || false}
                onChange={handleToggleQQNotify}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
        <div className="feature-card-body">
          <p className="help-text">
            开启后，开播、下播、动态、视频等通知会推送到下方配置的 QQ 群或私聊
          </p>
          <p className="help-text" style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
            💡 QQ 推送与直播间功能（自动回复、欢迎等）相互独立，可单独开启
          </p>
        </div>
      </div>

      {/* 直播间功能状态提示 */}
      <div className={`status-banner ${room.enabled ? 'status-banner-enabled' : 'status-banner-disabled'}`}>
        <span className="status-icon">{room.enabled ? '🎙️' : '💤'}</span>
        <span className="status-text">
          {room.enabled
            ? '直播间功能已开启（自动回复、欢迎、感谢等）'
            : '直播间功能未开启（仅 QQ 推送生效）'}
        </span>
        <a href="/admin/bot" className="status-link">前往配置 →</a>
      </div>

      {/* 推送目标列表 */}
      <div className="qq-targets-section">
        <div className="section-header-row">
          <h3>📋 推送目标列表</h3>
          <div className="section-header-actions">
            {!isAdmin && maxQQGroups > 0 && (
              <div className="qq-quota-badge">
                <span className="quota-icon">📱</span>
                <span className="quota-label">推送配额</span>
                <span className={`quota-value ${currentTargetCount >= maxQQGroups ? 'at-limit' : ''}`}>
                  {currentTargetCount} / {maxQQGroups}
                </span>
              </div>
            )}
            <button 
              className="btn btn-success"
              onClick={() => setShowAddTarget(true)}
              disabled={!canAddMore}
              title={!canAddMore ? `已达到推送目标上限 (${maxQQGroups})` : '添加推送目标'}
            >
              ➕ 添加推送目标
            </button>
          </div>
        </div>

        {(!room.qq_targets || room.qq_targets.length === 0) ? (
          <div className="no-targets">
            <p>暂无推送目标</p>
            <p className="help-text">点击上方按钮添加 QQ 群或私聊推送</p>
          </div>
        ) : (
          <div className="qq-targets-list">
            {room.qq_targets.map((target, index) => (
              <QQTargetCard 
                key={index}
                target={target}
                index={index}
                onEdit={() => setEditingTarget(index)}
                onDelete={() => handleDeleteTarget(index)}
              />
            ))}
          </div>
        )}
      </div>

      {/* 添加推送目标模态框 */}
      {showAddTarget && (
        <QQTargetEditModal
          target={null}
          onClose={() => setShowAddTarget(false)}
          onSave={handleAddTarget}
          saveLoading={saveLoading}
        />
      )}

      {/* 编辑推送目标模态框 */}
      {editingTarget !== null && (
        <QQTargetEditModal
          target={room.qq_targets[editingTarget]}
          onClose={() => setEditingTarget(null)}
          onSave={(updatedTarget) => handleUpdateTarget(editingTarget, updatedTarget)}
          saveLoading={saveLoading}
        />
      )}
    </div>
  );
};

// QQ Target Card Component
const QQTargetCard = ({ target, index, onEdit, onDelete }) => {
  const getTargetTypeName = (type) => {
    return type === 1 ? 'QQ 群' : '私聊';
  };

  const getEnabledFeatures = (target) => {
    const features = [];
    if (target.live_on?.enabled) features.push('开播通知');
    if (target.live_off?.enabled) features.push('下播通知');
    if (target.dynamic?.enabled) features.push('动态推送');
    if (target.video?.enabled) features.push('视频推送');
    if (target.offline_gift?.enabled) features.push('下播礼物');
    return features;
  };

  const enabledFeatures = getEnabledFeatures(target);

  return (
    <div className="qq-target-card">
      <div className="qq-target-card-header">
        <div className="qq-target-info">
          <span className="qq-target-type-badge">
            {target.type === 1 ? '👥 群' : '💬 私聊'}
          </span>
          <span className="qq-target-id">{target.id}</span>
        </div>
        <div className="qq-target-actions">
          <button className="btn btn-sm btn-primary" onClick={onEdit}>
            ✏️ 编辑
          </button>
          <button className="btn btn-sm btn-danger" onClick={onDelete}>
            🗑️
          </button>
        </div>
      </div>
      
      <div className="qq-target-card-body">
        <div className="qq-target-features">
          {enabledFeatures.length > 0 ? (
            enabledFeatures.map((feature, idx) => (
              <span key={idx} className="feature-badge feature-enabled">
                {feature}
              </span>
            ))
          ) : (
            <span className="feature-badge feature-disabled">未启用任何通知</span>
          )}
        </div>
        
        {target.live_off?.send_report && (
          <div className="qq-target-extra">
            <span className="extra-badge">📊 发送直播报告</span>
            {target.report_config && (
              <span className="extra-badge extra-badge-secondary">
                {getReportConfigSummary(target.report_config)}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// 获取报告配置摘要
const getReportConfigSummary = (reportConfig) => {
  if (!reportConfig) return '默认配置';
  
  const hiddenItems = [];
  if (reportConfig.show_total_income === false) hiddenItems.push('总营收');
  if (reportConfig.show_follower_stats === false) hiddenItems.push('粉丝变化');
  if (reportConfig.show_stats_cards === false) hiddenItems.push('统计卡片');
  if (reportConfig.show_danmaku_rank === false) hiddenItems.push('弹幕排行');
  if (reportConfig.show_gift_rank === false) hiddenItems.push('礼物排行');
  if (reportConfig.show_blind_box === false) hiddenItems.push('盲盒');
  if (reportConfig.show_sc_rank === false) hiddenItems.push('SC');
  if (reportConfig.show_guard === false) hiddenItems.push('大航海');
  if (reportConfig.show_medal_upgrade === false) hiddenItems.push('粉丝牌升级');
  if (reportConfig.show_wordcloud === false) hiddenItems.push('词云');
  
  if (hiddenItems.length === 0) return '显示全部';
  if (hiddenItems.length <= 2) return `隐藏: ${hiddenItems.join(', ')}`;
  return `隐藏 ${hiddenItems.length} 项`;
};

// QQ Target Edit Modal Component
const QQTargetEditModal = ({ target, onClose, onSave, saveLoading }) => {
  const [editData, setEditData] = useState(target || {
    type: 1,
    id: 0,
    live_on: {
      enabled: true,
      message: "{uname}开播了{newline}{uname} 正在直播 {title}{newline}{url}{next}{cover}"
    },
    live_off: {
      enabled: true,
      message: "{uname} 下播了~本次直播 {duration}",
      send_report: false
    },
    dynamic: {
      enabled: true,
      message: "{uname} 发布了新动态{url}{next}{card_image}"
    },
    video: {
      enabled: true,
      message: "{uname} 投稿了新视频📺 {title}{newline}{url}{next}{card_image}"
    },
    report_config: {
      show_total_income: true,
      show_follower_stats: true,
      show_stats_cards: true,
      show_danmaku_rank: true,
      show_gift_rank: true,
      show_blind_box: true,
      show_sc_rank: true,
      show_guard: true,
      show_medal_upgrade: true,
      show_wordcloud: true
    },
    command_permissions: {
      '我的数据': { enabled: true, admin_only: false },
      '我的总数据': { enabled: true, admin_only: false },
      '直播间数据': { enabled: true, admin_only: false },
      '直播间总数据': { enabled: true, admin_only: false },
      '直播报告': { enabled: true, admin_only: true },
      '上场报告': { enabled: true, admin_only: true },
      '贡献榜': { enabled: true, admin_only: false },
      '弹幕榜': { enabled: true, admin_only: false },
      '盲盒榜': { enabled: true, admin_only: false },
      '大航海榜': { enabled: true, admin_only: false },
      'sc榜': { enabled: true, admin_only: false }
    },
    offline_gift: {
      enabled: false,
      min_battery: 10,
      include_blind_box: true,
      include_guard: true,
      include_normal_gift: true,
      message: "{user} 在下播后送了 {num}个{gift}，价值{value}电池~{next}{screenshot}",
      message_blind_box: "{user} 在下播后开了 {box_count}个{blind_gift}，{profit_text}{next}{screenshot}",
      message_guard: "{user} 在下播后开通了 {num}个{unit}的{guard_name}~{next}{screenshot}"
    }
  });

  const handleSave = () => {
    if (!editData.id || editData.id <= 0) {
      alert('请输入有效的 QQ 号或群号');
      return;
    }
    onSave(editData);
  };

  const updateField = (field, value) => {
    setEditData({ ...editData, [field]: value });
  };

  const updateNestedField = (category, field, value) => {
    setEditData({
      ...editData,
      [category]: {
        ...editData[category],
        [field]: value
      }
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{target ? '编辑推送目标' : '添加推送目标'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        
        <div className="modal-body">
          {/* 基础设置 */}
          <div className="form-section">
            <h4>基础设置</h4>
            
            <FormField label="推送类型">
              <select
                className="form-input"
                value={editData.type}
                onChange={(e) => updateField('type', parseInt(e.target.value))}
              >
                <option value={1}>QQ 群</option>
                <option value={2}>私聊</option>
              </select>
            </FormField>

            <FormField label={editData.type === 1 ? "QQ 群号" : "QQ 号"}>
              <input
                type="number"
                className="form-input"
                value={editData.id || ''}
                onChange={(e) => updateField('id', parseInt(e.target.value) || 0)}
                placeholder={editData.type === 1 ? "请输入 QQ 群号" : "请输入 QQ 号"}
              />
            </FormField>
          </div>

          {/* 开播通知 */}
          <div className="form-section">
            <h4>🔴 开播通知</h4>
            
            <FormField label="启用" inline>
              <ToggleSwitch 
                checked={editData.live_on?.enabled || false}
                onChange={(checked) => updateNestedField('live_on', 'enabled', checked)}
              />
            </FormField>

            {editData.live_on?.enabled && (
              <FormField label="消息模板">
                <textarea
                  className="form-input"
                  value={editData.live_on?.message || ''}
                  onChange={(e) => updateNestedField('live_on', 'message', e.target.value)}
                  rows="4"
                  placeholder="{atall}{newline}🎉 {uname}开播了！{newline}🎬 {title}{newline}🔗 {url}{next}{cover}"
                />
                <div className="template-help">
                  <p className="help-text"><strong>变量说明:</strong></p>
                  <p className="help-text">{'{uname}'} 主播名称 • {'{title}'} 直播标题 • {'{url}'} 直播链接 • {'{room_id}'} 房间号 • {'{cover}'} 封面图片</p>
                  <p className="help-text"><strong>特殊变量:</strong></p>
                  <p className="help-text">{'{next}'} 分条发送 • {'{newline}'} 同条消息内换行 • {'{atall}'} @全体成员</p>
                </div>
              </FormField>
            )}
          </div>

          {/* 下播通知 */}
          <div className="form-section">
            <h4>⚫ 下播通知</h4>
            
            <FormField label="启用" inline>
              <ToggleSwitch 
                checked={editData.live_off?.enabled || false}
                onChange={(checked) => updateNestedField('live_off', 'enabled', checked)}
              />
            </FormField>

            {editData.live_off?.enabled && (
              <>
                <FormField label="消息模板">
                  <textarea
                    className="form-input"
                    value={editData.live_off?.message || ''}
                    onChange={(e) => updateNestedField('live_off', 'message', e.target.value)}
                    rows="3"
                    placeholder="📴 {uname} 下播了~{newline}本次直播时长：{duration}"
                  />
                  <div className="template-help">
                    <p className="help-text"><strong>变量说明:</strong></p>
                    <p className="help-text">{'{uname}'} 主播名称 • {'{duration}'} 直播时长</p>
                    <p className="help-text"><strong>特殊变量:</strong></p>
                    <p className="help-text">{'{next}'} 分条发送 • {'{newline}'} 同条消息内换行</p>
                  </div>
                </FormField>

                <FormField label="发送直播报告" inline>
                  <ToggleSwitch 
                    checked={editData.live_off?.send_report || false}
                    onChange={(checked) => updateNestedField('live_off', 'send_report', checked)}
                  />
                </FormField>
                <p className="help-text">下播时同时发送本场直播的数据统计报告图片</p>

                {/* 直播报告配置 */}
                {editData.live_off?.send_report && (
                  <div className="report-config-section">
                    <h5>📊 直播报告显示配置</h5>
                    <p className="help-text">配置此推送目标收到的直播报告中显示哪些内容</p>
                    
                    <div className="report-config-grid">
                      <FormField label="显示总营收" inline>
                        <ToggleSwitch 
                          checked={editData.report_config?.show_total_income !== false}
                          onChange={(checked) => updateNestedField('report_config', 'show_total_income', checked)}
                        />
                      </FormField>
                      
                      <FormField label="显示粉丝/航海变化" inline>
                        <ToggleSwitch 
                          checked={editData.report_config?.show_follower_stats !== false}
                          onChange={(checked) => updateNestedField('report_config', 'show_follower_stats', checked)}
                        />
                      </FormField>
                      
                      <FormField label="显示数据统计卡片" inline>
                        <ToggleSwitch 
                          checked={editData.report_config?.show_stats_cards !== false}
                          onChange={(checked) => updateNestedField('report_config', 'show_stats_cards', checked)}
                        />
                      </FormField>
                      
                      <FormField label="显示弹幕排行" inline>
                        <ToggleSwitch 
                          checked={editData.report_config?.show_danmaku_rank !== false}
                          onChange={(checked) => updateNestedField('report_config', 'show_danmaku_rank', checked)}
                        />
                      </FormField>
                      
                      <FormField label="显示礼物排行" inline>
                        <ToggleSwitch 
                          checked={editData.report_config?.show_gift_rank !== false}
                          onChange={(checked) => updateNestedField('report_config', 'show_gift_rank', checked)}
                        />
                      </FormField>
                      
                      <FormField label="显示盲盒统计" inline>
                        <ToggleSwitch 
                          checked={editData.report_config?.show_blind_box !== false}
                          onChange={(checked) => updateNestedField('report_config', 'show_blind_box', checked)}
                        />
                      </FormField>
                      
                      <FormField label="显示SC排行" inline>
                        <ToggleSwitch 
                          checked={editData.report_config?.show_sc_rank !== false}
                          onChange={(checked) => updateNestedField('report_config', 'show_sc_rank', checked)}
                        />
                      </FormField>
                      
                      <FormField label="显示大航海列表" inline>
                        <ToggleSwitch 
                          checked={editData.report_config?.show_guard !== false}
                          onChange={(checked) => updateNestedField('report_config', 'show_guard', checked)}
                        />
                      </FormField>
                      
                      <FormField label="显示粉丝牌升级" inline>
                        <ToggleSwitch 
                          checked={editData.report_config?.show_medal_upgrade !== false}
                          onChange={(checked) => updateNestedField('report_config', 'show_medal_upgrade', checked)}
                        />
                      </FormField>
                      
                      <FormField label="显示弹幕词云" inline>
                        <ToggleSwitch 
                          checked={editData.report_config?.show_wordcloud !== false}
                          onChange={(checked) => updateNestedField('report_config', 'show_wordcloud', checked)}
                        />
                      </FormField>
                      
                      <FormField label="显示排行具体数值" inline>
                        <ToggleSwitch 
                          checked={editData.report_config?.show_rank_values !== false}
                          onChange={(checked) => updateNestedField('report_config', 'show_rank_values', checked)}
                        />
                      </FormField>
                    </div>
                    <p className="help-text">💡 关闭「显示排行具体数值」后，排行榜将只显示用户名不显示具体电池/弹幕数量</p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* 动态推送 */}
          <div className="form-section">
            <h4>📝 动态推送</h4>
            
            <FormField label="启用" inline>
              <ToggleSwitch 
                checked={editData.dynamic?.enabled || false}
                onChange={(checked) => updateNestedField('dynamic', 'enabled', checked)}
              />
            </FormField>

            {editData.dynamic?.enabled && (
              <FormField label="消息模板">
                <textarea
                  className="form-input"
                  value={editData.dynamic?.message || ''}
                  onChange={(e) => updateNestedField('dynamic', 'message', e.target.value)}
                  rows="3"
                  placeholder="📢 {uname} 发布了新动态{next}{card_image}"
                />
                <div className="template-help">
                  <p className="help-text"><strong>变量说明:</strong></p>
                  <p className="help-text">{'{uname}'} 主播名称 • {'{content}'} 动态内容文字 • {'{url}'} 动态链接 • {'{card_image}'} 动态卡片图片</p>
                  <p className="help-text"><strong>特殊变量:</strong></p>
                  <p className="help-text">{'{next}'} 分条发送 • {'{newline}'} 同条消息内换行 • {'{atall}'} @全体成员</p>
                </div>
              </FormField>
            )}
          </div>

          {/* 视频推送 */}
          <div className="form-section">
            <h4>🎬 视频推送</h4>
            
            <FormField label="启用" inline>
              <ToggleSwitch 
                checked={editData.video?.enabled || false}
                onChange={(checked) => updateNestedField('video', 'enabled', checked)}
              />
            </FormField>

            {editData.video?.enabled && (
              <FormField label="消息模板">
                <textarea
                  className="form-input"
                  value={editData.video?.message || ''}
                  onChange={(e) => updateNestedField('video', 'message', e.target.value)}
                  rows="3"
                  placeholder="🎬 {uname} 投稿了新视频{newline}📺 {title}{next}{url}{next}{cover}"
                />
                <div className="template-help">
                  <p className="help-text"><strong>变量说明:</strong></p>
                  <p className="help-text">{'{uname}'} 主播名称 • {'{title}'} 视频标题 • {'{url}'} 视频链接 • {'{cover}'} 视频封面</p>
                  <p className="help-text"><strong>特殊变量:</strong></p>
                  <p className="help-text">{'{next}'} 分条发送 • {'{newline}'} 同条消息内换行 • {'{atall}'} @全体成员</p>
                </div>
              </FormField>
            )}
          </div>

          {/* 下播礼物推送 */}
          <div className="form-section">
            <h4>🎁 下播礼物推送</h4>
            <p className="help-text" style={{marginBottom: '12px'}}>
              主播下播后收到的礼物会推送到此目标
            </p>

            <FormField label="启用" inline>
              <ToggleSwitch
                checked={editData.offline_gift?.enabled || false}
                onChange={(checked) => updateNestedField('offline_gift', 'enabled', checked)}
              />
            </FormField>

            {editData.offline_gift?.enabled && (
              <>
                <FormField label="最低电池阈值">
                  <input
                    type="number"
                    className="form-input"
                    value={editData.offline_gift?.min_battery || 0}
                    onChange={(e) => updateNestedField('offline_gift', 'min_battery', parseInt(e.target.value) || 0)}
                    placeholder="10"
                    min="0"
                  />
                  <p className="help-text">低于此电池数的礼物不会推送</p>
                </FormField>

                <div className="checkbox-group" style={{marginBottom: '16px'}}>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editData.offline_gift?.include_normal_gift !== false}
                      onChange={(e) => updateNestedField('offline_gift', 'include_normal_gift', e.target.checked)}
                    />
                    <span>普通礼物</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editData.offline_gift?.include_blind_box !== false}
                      onChange={(e) => updateNestedField('offline_gift', 'include_blind_box', e.target.checked)}
                    />
                    <span>盲盒礼物</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editData.offline_gift?.include_guard !== false}
                      onChange={(e) => updateNestedField('offline_gift', 'include_guard', e.target.checked)}
                    />
                    <span>大航海</span>
                  </label>
                </div>

                <FormField label="普通礼物消息模板">
                  <textarea
                    className="form-input"
                    value={editData.offline_gift?.message || ''}
                    onChange={(e) => updateNestedField('offline_gift', 'message', e.target.value)}
                    rows="2"
                    placeholder="{user} 在下播后送了 {num}个{gift}，价值{value}电池~"
                  />
                </FormField>

                <FormField label="盲盒礼物消息模板">
                  <textarea
                    className="form-input"
                    value={editData.offline_gift?.message_blind_box || ''}
                    onChange={(e) => updateNestedField('offline_gift', 'message_blind_box', e.target.value)}
                    rows="2"
                    placeholder="{user} 在下播后开了 {box_count}个{blind_gift}，{profit_text}"
                  />
                </FormField>

                <FormField label="大航海消息模板">
                  <textarea
                    className="form-input"
                    value={editData.offline_gift?.message_guard || ''}
                    onChange={(e) => updateNestedField('offline_gift', 'message_guard', e.target.value)}
                    rows="2"
                    placeholder="{user} 在下播后开通了 {num}个{unit}的{guard_name}~"
                  />
                </FormField>

                <div className="template-help">
                  <p className="help-text"><strong>变量说明:</strong></p>
                  <p className="help-text">{'{user}'} 用户名 • {'{gift}'} 礼物名 • {'{num}'} 数量 • {'{value}'} 电池价值</p>
                  <p className="help-text">{'{box_count}'} 盲盒数量 • {'{blind_gift}'} 盲盒名称 • {'{profit_text}'} 盈亏描述</p>
                  <p className="help-text">{'{guard_name}'} 大航海等级 • {'{unit}'} 时间单位 • {'{newline}'} 换行 • {'{next}'} 分条发送</p>
                  <p className="help-text">{'{screenshot}'} 礼物截图（在消息中使用此变量会自动生成截图）</p>
                </div>
              </>
            )}
          </div>

          {/* 指令权限配置 - 仅对群推送显示 */}
          {editData.type === 1 && (
            <div className="form-section">
              <h4>🔐 指令权限配置</h4>
              <p className="help-text" style={{marginBottom: '12px'}}>
                配置该群可用的指令及权限限制。关闭的指令在该群无法使用，启用「仅管理员」后只有群主和管理员可使用。
              </p>
              
              <div className="permission-grid">
                {[
                  { key: '我的数据', label: '我的数据', desc: '查询本场个人投喂' },
                  { key: '我的总数据', label: '我的总数据', desc: '查询累计个人投喂' },
                  { key: '直播间数据', label: '直播间数据', desc: '查询本场直播间数据' },
                  { key: '直播间总数据', label: '直播间总数据', desc: '查询累计直播间数据' },
                  { key: '直播报告', label: '直播报告', desc: '生成直播报告图片' },
                  { key: '上场报告', label: '上场报告', desc: '查看上场直播报告' },
                  { key: '贡献榜', label: '贡献榜', desc: '查看贡献排行榜' },
                  { key: '弹幕榜', label: '弹幕榜', desc: '查看弹幕排行榜' },
                  { key: '盲盒榜', label: '盲盒榜', desc: '查看盲盒排行榜' },
                  { key: '大航海榜', label: '大航海榜', desc: '查看大航海排行榜' },
                  { key: 'sc榜', label: 'SC榜', desc: '查看SC排行榜' }
                ].map(cmd => {
                  const perm = editData.command_permissions?.[cmd.key] || { enabled: true, admin_only: false };
                  return (
                    <div key={cmd.key} className="permission-item">
                      <div className="permission-info">
                        <span className="permission-name">!{cmd.label}</span>
                        <span className="permission-desc">{cmd.desc}</span>
                      </div>
                      <div className="permission-controls">
                        <label className="permission-toggle" title="启用/禁用该指令">
                          <input
                            type="checkbox"
                            checked={perm.enabled !== false}
                            onChange={(e) => {
                              const newPerms = { ...editData.command_permissions };
                              newPerms[cmd.key] = { ...perm, enabled: e.target.checked };
                              setEditData({ ...editData, command_permissions: newPerms });
                            }}
                          />
                          <span className="toggle-label">{perm.enabled !== false ? '启用' : '禁用'}</span>
                        </label>
                        <label className="permission-toggle admin-toggle" title="仅管理员可用">
                          <input
                            type="checkbox"
                            checked={perm.admin_only === true}
                            disabled={perm.enabled === false}
                            onChange={(e) => {
                              const newPerms = { ...editData.command_permissions };
                              newPerms[cmd.key] = { ...perm, admin_only: e.target.checked };
                              setEditData({ ...editData, command_permissions: newPerms });
                            }}
                          />
                          <span className="toggle-label">仅管理员</span>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 变量速查表 */}
          <div className="form-section template-reference">
            <h4>📖 变量速查表</h4>
            <div className="reference-grid">
              <div className="reference-item">
                <code>{'{next}'}</code>
                <span>分条发送 - 将消息分割成多条独立消息</span>
              </div>
              <div className="reference-item">
                <code>{'{newline}'}</code>
                <span>换行 - 在同一条消息内换行</span>
              </div>
              <div className="reference-item">
                <code>{'{atall}'}</code>
                <span>@全体成员 - 需要机器人有管理员权限</span>
              </div>
              <div className="reference-item">
                <code>{'{cover}'}</code>
                <span>封面图片 - 自动转为图片消息</span>
              </div>
              <div className="reference-item">
                <code>{'{card_image}'}</code>
                <span>卡片图片 - 渲染动态/视频卡片</span>
              </div>
              <div className="reference-item">
                <code>{'{pathpic=xxx}'}</code>
                <span>自定义图片 - 发送指定路径的图片</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            取消
          </button>
          <button 
            className="btn btn-success" 
            onClick={handleSave}
            disabled={saveLoading}
          >
            {saveLoading ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Form Field Component
const FormField = ({ label, children, inline }) => {
  return (
    <div className={`form-field ${inline ? 'form-field-inline' : ''}`}>
      <label className="form-label">{label}</label>
      <div className="form-control">
        {children}
      </div>
    </div>
  );
};

// Toggle Switch Component
const ToggleSwitch = ({ checked, onChange }) => {
  return (
    <label className="toggle-switch">
      <input 
        type="checkbox" 
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="toggle-slider"></span>
    </label>
  );
};

export default QQBotConfig;
